import { AssetCategory } from '../../../Utils/accountUtils';
import {
  AccountStatusType,
  analyticsStatusDataRefType,
  FIType,
} from '../../../Utils/types';
import { doesFiTypeExist } from './assetUtils';
import { FAILED_STATUSES } from './constants';

export const getTotalFixedDepositValue = (
  statusData: any,
  data: any,
  datafromAnalyticsStatus: React.RefObject<analyticsStatusDataRefType | null>
): number => {
  if (!doesFiTypeExist(AssetCategory.FIXED_DEPOSIT, statusData)) {
    return 0;
  }

  const depositSummary = data.current?.FD_RD?.depositSummary;
  const totalValue =
    depositSummary?.totalCurrentReturns != null &&
    depositSummary?.totalInvestedValue != null
      ? Number(depositSummary.totalCurrentReturns) +
        Number(depositSummary.totalInvestedValue)
      : (datafromAnalyticsStatus.current?.fdRd ?? 0);

  return Math.round(totalValue);
};

// rawData never reliably carries fipId/fipName, so those are resolved from
// the linked account (statusData) instead, keyed by lastRawDataId.
export const getDepositData = (depositData: any, statusData?: any) => {
  const fixedDeposit: any[] = [];
  const recurringDeposit: any[] = [];
  let totalInterestEarned = 0;

  const rawEntryByRequestId = new Map<string, any>();
  (depositData || []).forEach((item: any) => {
    if (item?.dataRequestDetailsId) {
      rawEntryByRequestId.set(String(item.dataRequestDetailsId), item);
    }
  });

  const fdrdAccounts = (statusData?.current?.accountDetails ?? []).filter(
    (acc: any) =>
      (acc.fiType === FIType.TERM_DEPOSIT ||
        acc.fiType === FIType.TERM_DEPOSIT_HYPHEN ||
        acc.fiType === FIType.RECURRING_DEPOSIT) &&
      acc.status === AccountStatusType.ACTIVE
  );

  fdrdAccounts.forEach((acc: any) => {
    const targetList =
      acc.fiType === FIType.RECURRING_DEPOSIT ? recurringDeposit : fixedDeposit;

    if (FAILED_STATUSES.includes(acc.lastRequestFIStatus)) {
      targetList.push({
        fipId: acc.fipId || '',
        institutionName: acc.fipName || '',
        totalInvestedAmount: acc.closingBalance || 0,
        totalPortfolioValue: acc.closingBalance || 0,
        interestRate: 0,
        maturityAmount: 0,
        dateOfMaturity: null,
        maskedAccNumber: acc.maskedAccountNumber || '',
        lastRequestFIStatus: acc.lastRequestFIStatus,
      });
      return;
    }

    const item = rawEntryByRequestId.get(String(acc.lastRawDataId));
    if (!item) return;

    const summary = item.rawData?.Summary || {};
    const deposit = {
      fipId: acc.fipId || '',
      institutionName: acc.fipName || '',
      totalInvestedAmount: parseFloat(summary.principalAmount || '0'),
      totalPortfolioValue: parseFloat(
        summary.currentValue || summary.currentBalance || '0'
      ),
      interestRate: summary.interestRate || 0,
      maturityAmount: parseFloat(summary.maturityAmount || '0'),
      dateOfMaturity: summary.maturityDate || null,
      maskedAccNumber: item.rawData?.maskedAccNumber || '',
      branch: summary.branch || '',
      tenure: summary.tenureYears
        ? `${summary.tenureYears}Y ${summary.tenureMonths || '0'}M`
        : '',
      interestPayout: summary.interestPayout || '',
      interestOnMaturity: parseFloat(summary.interestOnMaturity || '0'),
      openingDate: summary.openingDate || null,
      accountType: summary.accountType || '',
    };
    totalInterestEarned += Math.max(
      0,
      deposit.totalPortfolioValue - deposit.totalInvestedAmount
    );

    if (acc.fiType === FIType.RECURRING_DEPOSIT) {
      targetList.push({
        ...deposit,
        recurringAmount: summary.recurringAmount || null,
      });
    } else {
      targetList.push(deposit);
    }
  });

  return {
    fixedDeposit,
    recurringDeposit,
    totalInterestEarned,
  };
};
